/**
 * TimezoneSelector — Searchable grouped timezone selector
 *
 * Groups timezones by region (Americas, Europe, Asia, etc.) and shows
 * UTC offset next to each timezone name: "(UTC+07:00) Asia/Bangkok"
 *
 * Uses shadcn/ui Popover + custom search input since no Command component is available.
 *
 * [T114-F3-h] Timezone standardization
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, ChevronsUpDown, Clock, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchTimezones } from '@/features/timezone/timezoneSlice';
import type { TimezoneGroup, TimezoneInfo } from '@/lib/types';

interface TimezoneSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}

export function TimezoneSelector({ value, onValueChange, label, disabled }: TimezoneSelectorProps) {
  const dispatch = useAppDispatch();
  const { groups, isLoading } = useAppSelector((state) => state.timezone);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchTimezones());
  }, [dispatch]);

  // Find the selected timezone's display label
  const selectedLabel = useMemo(() => {
    for (const group of groups) {
      const zone = group.zones.find((z) => z.name === value);
      if (zone) return `(${zone.offset}) ${zone.label}`;
    }
    return value || 'Select timezone…';
  }, [groups, value]);

  // Filter groups by search term
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const lowerSearch = search.toLowerCase();
    return groups
      .map((group: TimezoneGroup) => ({
        ...group,
        zones: group.zones.filter(
          (z: TimezoneInfo) =>
            z.name.toLowerCase().includes(lowerSearch) ||
            z.label.toLowerCase().includes(lowerSearch) ||
            z.offset.toLowerCase().includes(lowerSearch) ||
            group.region.toLowerCase().includes(lowerSearch)
        ),
      }))
      .filter((group) => group.zones.length > 0);
  }, [groups, search]);

  if (isLoading && groups.length === 0) {
    return (
      <div className="space-y-2">
        {label && <Label>{label}</Label>}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid="timezone-selector-trigger"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{selectedLabel}</span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start">
          {/* Search input */}
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              placeholder="Search timezones…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 p-0 shadow-none focus-visible:ring-0"
              data-testid="timezone-search-input"
            />
          </div>
          <ScrollArea className="h-[300px]">
            {filteredGroups.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No timezones found.</p>
            ) : (
              <div className="p-1">
                {filteredGroups.map((group) => (
                  <div key={group.region}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground sticky top-0 bg-popover">
                      {group.region}
                    </div>
                    {group.zones.map((zone) => (
                      <button
                        key={zone.name}
                        className={cn(
                          'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus:bg-accent focus:text-accent-foreground',
                          'min-h-[44px]', // Touch target
                          value === zone.name && 'bg-accent'
                        )}
                        onClick={() => {
                          onValueChange(zone.name);
                          setOpen(false);
                          setSearch('');
                        }}
                        data-testid={`timezone-option-${zone.name}`}
                      >
                        <span className={cn(
                          'absolute left-2 flex h-3.5 w-3.5 items-center justify-center',
                          value !== zone.name && 'opacity-0'
                        )}>
                          <Check className="h-4 w-4" />
                        </span>
                        <span className="text-xs text-muted-foreground w-[90px] shrink-0">
                          {zone.offset}
                        </span>
                        <span className="truncate">{zone.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}
